import { Text } from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import React, { memo, useMemo } from "react";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import { MESSAGE_STATE } from "modules/utils/Constants";
import SystemMessage from "../messages/system-messages/system-message";
import UserMessage from "../messages/user-message";
import HappycoWorkOrderMessage from "modules/external/happyco/happyco-work-order-message";
import HappycoMessage from "modules/external/happyco/happyco-message";
import TaskMessage from "../tasks/task-message";

// Augmenting the MessageFragment interface to include isDraft
// Tried to use a state value but it was causing issues with the Virtuoso list
// where the messages would re-render and flash
export interface IMessageFragmentWithIsDraft extends MessageFragment {
  isDraft?: boolean;
}

interface IProps {
  messageThread: MessageThreadFragment;
  message: IMessageFragmentWithIsDraft;
  messageState: MESSAGE_STATE;
}

const MessageThreadMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const { isMeRemoved } = useRemovedFromThread(messageThread);

  const _messageState: MESSAGE_STATE = useMemo(() => {
    if (isMeRemoved) {
      return "read-only";
    }
    return messageState;
  }, [isMeRemoved, messageState]);

  if (message.type === MessageType.System) {
    return <SystemMessage messageThread={messageThread} message={message} />;
  }

  if (message.type === MessageType.User) {
    return (
      <UserMessage
        messageThread={messageThread}
        message={message}
        messageState={_messageState}
      />
    );
  }

  if (message.type === MessageType.Email) {
    return (
      <UserMessage
        messageThread={messageThread}
        message={message}
        messageState={_messageState}
      />
    );
  }

  if (message.type === MessageType.Task) {
    return (
      <TaskMessage
        messageThread={messageThread}
        message={message}
        messageState={_messageState}
      />
    );
  }

  if (message.type === MessageType.External) {
    switch (message.subType) {
      case "HAPPYCO_WORK_ORDER_MESSAGE": {
        return (
          <HappycoWorkOrderMessage
            messageThread={messageThread}
            message={message}
            messageState={_messageState}
          />
        );
      }
      case "HAPPYCO_MESSAGE": {
        return (
          <HappycoMessage
            messageThread={messageThread}
            message={message}
            messageState={_messageState}
          />
        );
      }
      default: {
        return <Text>Unsupported message sub type: {message.subType}</Text>;
      }
    }
  }

  return null;
};

export default memo(MessageThreadMessage);
